/**
 * Static content, types, and routing logic for the
 * "Conclude a Membership" workflow.
 *
 * Language canon for this folder: plain spoken register, short sentences, no
 * dashes, no ministry vocabulary, no internal codes, and true dates only.
 */

export type ContestedAnswer = 'no' | 'yes' | 'not_sure';
export type EndingKind = 'withdrawal' | 'passing';
export type YesNo = 'yes' | 'no';

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  helper: string;
}

export const ORIENTATION_KNOW =
  'Membership ends the way it began: deliberately, in writing, with dignity. A departing member leaves with what they came with: themselves, their dignity, and the Covenant of Privacy, which survives departure in full. Contributions given remain given; there is nothing to buy out and nothing to take.';

export const ORIENTATION_DO =
  'Answer the questions. The portal prepares the papers this ending calls for, and the checklist that removes any authorities the same week.';

export const ORIENTATION_SCHEDULE =
  'Nothing recurring comes from an ending. The roster updates and the record closes.';

export const CONTESTED_QUESTION =
  'Is this ending contested, connected to any dispute, or something the member has not agreed to in writing?';

export const CONTESTED_STOP =
  'An ending the member has not freely chosen is a different procedure with its own careful path. Talk with the team, and the removal and discipline guidance carries the lanes.';

export const CONTESTED_OPTIONS: ChoiceOption<ContestedAnswer>[] = [
  { value: 'no', label: 'No', helper: 'The member has agreed to this in writing, and no dispute sits anywhere near it.' },
  { value: 'yes', label: 'Yes', helper: 'It is contested, or a dispute is connected to it.' },
  { value: 'not_sure', label: 'Not sure', helper: 'It might be. We would rather ask than guess.' },
];

export const ENDING_OPTIONS: ChoiceOption<EndingKind>[] = [
  { value: 'withdrawal', label: 'The member is withdrawing', helper: 'They have chosen to leave, and they will sign that choice.' },
  { value: 'passing', label: 'A member has passed away', helper: 'The membership concludes as of the date of death.' },
];

export const SEQUENCING_TEACHING =
  'Papers carry the true date they are signed. There is no back dating here, and the portal will not offer it.';

export const PASSING_FAMILY_NOTE =
  "The family's continuing place in the Association is a matter of their own membership, held or offered with all the warmth the moment calls for.";

export const AUTHORITY_NOTE =
  'Where the member held an office or banking authority, the checklist prepares with it, and the bank removal happens the same week.';

export interface ExitWorkflowData {
  contested?: ContestedAnswer;
  ending?: EndingKind;

  member_name?: string;
  effective_date?: string;
  held_office?: YesNo;
  held_banking?: YesNo;
  bank_name?: string;
  gratitude_line?: string;

  passing_date?: string;
  passing_authorities?: YesNo;
  remembrance_line?: string;

  notes?: string;
}

export interface ExitRouteResult {
  generatesNothing: boolean;
  hardStop: boolean;
  summary: string;
}

export function needsAuthorityChecklist(data: ExitWorkflowData): boolean {
  if (data.ending === 'withdrawal') {
    return data.held_office === 'yes' || data.held_banking === 'yes';
  }
  if (data.ending === 'passing') {
    return data.passing_authorities === 'yes';
  }
  return false;
}

export function routeExit(data: ExitWorkflowData): ExitRouteResult {
  if (data.contested === 'yes' || data.contested === 'not_sure') {
    return { generatesNothing: true, hardStop: true, summary: 'Nothing prepares here. Talk with the team first.' };
  }
  if (!data.ending) {
    return { generatesNothing: true, hardStop: false, summary: 'Choose which ending this is.' };
  }
  const withChecklist = needsAuthorityChecklist(data);
  if (data.ending === 'withdrawal') {
    return {
      generatesNothing: false,
      hardStop: false,
      summary: withChecklist
        ? 'Four papers prepare: the written withdrawal, the acknowledging resolution, the authority removal checklist, and the privacy survival letter.'
        : 'Three papers prepare: the written withdrawal, the acknowledging resolution, and the privacy survival letter.',
    };
  }
  return {
    generatesNothing: false,
    hardStop: false,
    summary: withChecklist
      ? 'Two papers prepare: the memorial minute entry and the authority removal checklist.'
      : 'One paper prepares: the memorial minute entry.',
  };
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
